import { Fragment } from 'react';
import type { EditTarget } from '@web-studio/shared-schemas';
import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import { pickBlockField } from '../../../lib/localized';
import { parseMenuColumns, type MenuColumn, type MenuItem } from '../../../lib/parseMenuColumns';
import { pickChrome } from '../../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName, MenuItemOrderCode, MenuItemPriceSlot } from '../MenuItemTags';
import { MenuLegalLegend } from '../MenuLegalLegend';
import {
  isMenuColumnActive,
  isMenuItemActive,
  menuItemHasContent,
  resolveColumnImageUrl,
} from '../menuEditHelpers';

function countVisibleItems(column: MenuColumn): number {
  return column.items.filter((item) => menuItemHasContent(item)).length;
}

function StreetBoardItem({
  item,
  columnIndex,
  itemIndex,
  editable,
  activeEditTarget,
  locale,
}: {
  item: MenuItem;
  columnIndex: number;
  itemIndex: number;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
}) {
  if (!menuItemHasContent(item)) {
    return null;
  }
  const itemActive = isMenuItemActive(
    activeEditTarget?.kind === 'menu-item' ? activeEditTarget : null,
    columnIndex,
    itemIndex,
  );
  return (
    <li>
      <article
        className={['ws-menu-street__item', itemActive ? 'ws-edit-child--active' : '']
          .filter(Boolean)
          .join(' ')}
        {...(editable
          ? {
              'data-edit-child': 'menu-item' as const,
              'data-column-index': String(columnIndex),
              'data-item-index': String(itemIndex),
            }
          : {})}
      >
        <div className="ws-menu-street__item-row">
          <MenuItemOrderCode code={item.code} className="ws-menu-street__item-code" />
          <div className="ws-menu-street__item-copy">
            <div className="ws-menu__item-primary">
              <MenuItemName item={item} locale={locale} showOrderCode={false} />
              <MenuItemDietaryTags item={item} locale={locale} iconsOnly placement="inline" />
            </div>
            {item.description ? (
              <p className="ws-menu-street__item-desc">{item.description}</p>
            ) : null}
          </div>
          <MenuItemPriceSlot price={item.price} className="ws-menu-street__item-price" />
        </div>
      </article>
    </li>
  );
}

/**
 * Street Board — Imbiss Wandtafel: category tiles (optional photo), one Abschnitt open.
 * Dishes stay typographic. Layout stays balanced with or without tile images.
 */
export function MenuStreetBoard({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const groupName = `menu-street-${block.blockId}`;
  const target = isActive ? activeEditTarget : null;
  const boardLabel = sectionTitle || pickChrome('menuCategories', locale);
  const countLabel = pickChrome('menuItemCount', locale);

  return (
    <section
      className={['ws-menu-street', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-menu-street__inner">
        <header className="ws-menu-street__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-street__title"
          >
            {sectionTitle}
          </EditableContent>
          <EditableContent
            fieldKey="sectionSubtitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-street__subtitle"
          >
            {sectionSubtitle}
          </EditableContent>
        </header>

        {columns.length > 0 ? (
          <div className="ws-menu-street__chrome" role="radiogroup" aria-label={boardLabel}>
            {columns.map((column, columnIndex) => {
              const columnActive = isMenuColumnActive(target, columnIndex);
              const imageUrl = resolveColumnImageUrl(block, column, columnIndex);
              const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : undefined;
              const imageSrcSet = imageUrl
                ? buildSiteImageSrcSet(imageUrl, [320, 480, 720, 960], 78)
                : undefined;
              const title = column.title.trim() || `Category ${columnIndex + 1}`;
              const count = countVisibleItems(column);
              const tabId = `${groupName}-label-${columnIndex}`;
              const panelId = `${groupName}-panel-${columnIndex}`;
              const imageFieldKey = `categoryImage${columnIndex}`;

              return (
                <Fragment key={`street-cat-${columnIndex}`}>
                  <input
                    type="radio"
                    name={groupName}
                    id={`${groupName}-tab-${columnIndex}`}
                    className="ws-menu-street__radio"
                    defaultChecked={columnIndex === 0}
                  />
                  <label
                    id={tabId}
                    htmlFor={`${groupName}-tab-${columnIndex}`}
                    className={[
                      'ws-menu-street__tile',
                      columnActive ? 'ws-edit-child--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    {...(editable
                      ? {
                          'data-edit-child': 'menu-column' as const,
                          'data-column-index': String(columnIndex),
                        }
                      : {})}
                  >
                    <span className="ws-menu-street__tile-visual">
                      {imageUrl ? (
                        <EditableMedia
                          fieldKey={imageFieldKey}
                          editable={editable}
                          activeEditTarget={target}
                          className="ws-menu-street__tile-frame"
                          src={imageSrc}
                          srcSet={imageSrcSet}
                          sizes={SITE_IMAGE_SIZES.menuCategory}
                          alt=""
                          width={720}
                          height={540}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="ws-menu-street__tile-placeholder" aria-hidden />
                      )}
                    </span>
                    <span className="ws-menu-street__tile-meta">
                      <span className="ws-menu-street__tile-title">{title}</span>
                      {count > 0 ? (
                        <span className="ws-menu-street__tile-count">
                          {count} {countLabel}
                        </span>
                      ) : null}
                    </span>
                  </label>
                  <div
                    className="ws-menu-street__panel"
                    id={panelId}
                    role="region"
                    aria-labelledby={tabId}
                  >
                    <h3 className="ws-menu-street__panel-title">{title}</h3>
                    <ul className="ws-menu-street__list">
                      {column.items.map((item, itemIndex) => (
                        <StreetBoardItem
                          key={`${item.name}-${itemIndex}`}
                          item={item}
                          columnIndex={columnIndex}
                          itemIndex={itemIndex}
                          editable={editable}
                          activeEditTarget={target}
                          locale={locale}
                        />
                      ))}
                    </ul>
                  </div>
                </Fragment>
              );
            })}
          </div>
        ) : null}

        <MenuLegalLegend columns={columns} locale={locale} />
      </div>
    </section>
  );
}
